/**
 * Página de Detalle de Cotización (Maestro-Detalle)
 *
 * Vista completa orquestada por useCotizacionDetalle:
 * - Datos de cabecera (cliente, fechas, estado)
 * - Tabla de items editable con drag & drop
 * - Notas con CRUD
 * - Sistema de revisiones con historial navegable
 * - PDF y envío por email
 *
 * @module pages/CotizacionDetalle
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import EntityModal from '../components/common/EntityModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import useCotizacionDetalle from '../hooks/useCotizacionDetalle';
import CotizacionHeader from '../components/cotizaciones/CotizacionHeader';
import CotizacionItemsTable from '../components/cotizaciones/CotizacionItemsTable';
import CotizacionTotales from '../components/cotizaciones/CotizacionTotales';
import CotizacionNotas from '../components/cotizaciones/CotizacionNotas';
import CotizacionRevisiones from '../components/cotizaciones/CotizacionRevisiones';
import '../styles/pages/cotizacion-detalle.css';

function CotizacionDetalle() {
  const { id } = useParams();
  const { hasPermission } = useAuth();

  const {
    cotizacion, items, revisiones, notas,
    loading, notasLoading,
    itemModalOpen, editingItem, notaModalOpen, editingNota,
    confirmItem, confirmNota, confirmEmail,
    isReordering, pdfLoading, emailLoading,
    showDescuentoForm, descuentoTipo, descuentoValor, savingDescuento,
    subtotal, total, descuentoAplicado,
    itemModalFields, notaModalFields,
    isEditable, canCreateRevision,
    handleCreateItem, handleEditItem, handleSaveItem, handleDeleteItem, confirmDeleteItem, closeItemModal,
    handleOpenDescuento, handleSaveDescuento, handleQuitarDescuento,
    cancelDescuento, setDescuentoTipo, setDescuentoValor,
    handleDragEnd,
    handleDownloadPdf, handleEnviarEmail, confirmEnviarEmail,
    handleBack, handleCrearRevision, handleVerRevision,
    handleCreateNota, handleEditNota, handleSaveNota, handleDeleteNota, confirmDeleteNota,
    closeNotaModal
  } = useCotizacionDetalle(id);

  if (loading && !cotizacion) {
    return (
      <>
        <Navbar />
        <div className="main-content">
          <div className="loading-spinner"><i className="fas fa-spinner fa-spin"></i> Cargando...</div>
        </div>
      </>
    );
  }

  if (!cotizacion) {
    return (
      <>
        <Navbar />
        <div className="main-content">
          <div className="alert alert-warning">Cotización no encontrada</div>
        </div>
      </>
    );
  }

  const canEditItems = isEditable && hasPermission('cotizaciones.update');
  const canDeleteItems = isEditable && hasPermission('cotizaciones.delete');
  const canDownloadPdf = hasPermission('cotizaciones.read');
  const canSendEmail = hasPermission('cotizaciones.update');
  const canCreateRevisionFlag = canCreateRevision && hasPermission('cotizaciones.create');

  return (
    <>
      <Navbar />
      <div className="main-content cotizacion-detalle">
        <CotizacionHeader
          cotizacion={cotizacion}
          total={total}
          onBack={handleBack}
          onDownloadPdf={handleDownloadPdf}
          onEnviarEmail={handleEnviarEmail}
          onCrearRevision={handleCrearRevision}
          pdfLoading={pdfLoading}
          emailLoading={emailLoading}
          canDownloadPdf={canDownloadPdf}
          canSendEmail={canSendEmail}
          canCreateRevision={canCreateRevisionFlag}
        />

        <CotizacionItemsTable
          items={items}
          canEdit={canEditItems}
          canDelete={canDeleteItems}
          isReordering={isReordering}
          onDragEnd={handleDragEnd}
          onCreate={handleCreateItem}
          onEdit={handleEditItem}
          onDelete={handleDeleteItem}
        >
          {items.length > 0 && (
            <CotizacionTotales
              cotizacion={cotizacion}
              isEditable={isEditable}
              subtotal={subtotal}
              total={total}
              descuentoAplicado={descuentoAplicado}
              showDescuentoForm={showDescuentoForm}
              descuentoTipo={descuentoTipo}
              descuentoValor={descuentoValor}
              savingDescuento={savingDescuento}
              onOpenDescuento={handleOpenDescuento}
              onSaveDescuento={handleSaveDescuento}
              onQuitarDescuento={handleQuitarDescuento}
              onSetDescuentoTipo={setDescuentoTipo}
              onSetDescuentoValor={setDescuentoValor}
              onCancelDescuento={cancelDescuento}
            />
          )}
        </CotizacionItemsTable>

        <CotizacionNotas
          notas={notas}
          loading={notasLoading}
          isEditable={isEditable}
          onCreate={handleCreateNota}
          onEdit={handleEditNota}
          onDelete={handleDeleteNota}
        />

        <CotizacionRevisiones
          revisiones={revisiones}
          cotizacionActualId={cotizacion.id}
          onVerRevision={handleVerRevision}
        />

        <EntityModal
          isOpen={itemModalOpen}
          onClose={closeItemModal}
          onSave={handleSaveItem}
          title={editingItem ? 'Editar Item' : 'Nuevo Item'}
          fields={itemModalFields}
          initialData={editingItem}
        />

        <ConfirmDialog
          isOpen={confirmItem.state.open}
          title="Confirmar Eliminación"
          message={confirmItem.state.item ? `¿Eliminar el item "${confirmItem.state.item.descripcion}"?` : ''}
          onConfirm={confirmDeleteItem}
          onCancel={confirmItem.close}
          confirmText="Eliminar"
          variant="danger"
        />

        <EntityModal
          isOpen={notaModalOpen}
          onClose={closeNotaModal}
          onSave={handleSaveNota}
          title={editingNota ? 'Editar Nota' : 'Nueva Nota'}
          fields={notaModalFields}
          initialData={editingNota}
        />

        <ConfirmDialog
          isOpen={confirmNota.state.open}
          title="Confirmar Eliminación"
          message={confirmNota.state.item
            ? `¿Eliminar la nota "${confirmNota.state.item.contenido.length > 50 ? confirmNota.state.item.contenido.substring(0, 50) + '...' : confirmNota.state.item.contenido}"?`
            : ''}
          onConfirm={confirmDeleteNota}
          onCancel={confirmNota.close}
          confirmText="Eliminar"
          variant="danger"
        />

        <ConfirmDialog
          isOpen={confirmEmail.state.open}
          title="Enviar Cotización por Email"
          message={`¿Confirma enviar la cotización ${cotizacion?.numero} al correo ${cotizacion?.cliente?.email}?`}
          onConfirm={confirmEnviarEmail}
          onCancel={confirmEmail.close}
          confirmText="Enviar"
        />
      </div>
    </>
  );
}

export default CotizacionDetalle;
